import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Paper, Stack, SwipeableDrawer, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { VoiceActivityApi, VoiceActivityDraft } from '@/api/apis/VoiceActivityApi';
import { ActivityApi } from '@/api/apis/ActivityApi';
import { ResponseError } from '@/api/runtime';
import { Activity } from '@/api/types/Activity';
import { AppContext } from '@/contexts/AppContext';
import { SupervisorContext } from '@/contexts/SupervisorContext';
import { useCharacter } from '@/contexts/CharacterContext';
import { useEffectiveChildMode } from '@/components/utils/useEffectiveChildMode';
import { CharacterMood } from '@/components/character';
import VoiceRecorder from './VoiceRecorder';
import ClarificationCard from './ClarificationCard';
import DayScheduleView from './DayScheduleView';

type VoiceState = 'idle' | 'recording' | 'processing' | 'clarifying' | 'schedule' | 'saving';

type VoiceActivityCreatorProps = {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
};

// Web Speech API — inte inkluderat i TypeScript DOM lib, definieras lokalt
type SpeechRecognitionResultItem = {
  readonly transcript: string;
  readonly confidence: number;
};

type SpeechRecognitionResult = {
  readonly isFinal: boolean;
  readonly length: number;
  item(index: number): SpeechRecognitionResultItem;
  [index: number]: SpeechRecognitionResultItem | undefined;
};

type SpeechRecognitionResultList = {
  readonly length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult | undefined;
};

type SpeechRecognitionEvent = {
  readonly resultIndex: number;
  readonly results: SpeechRecognitionResultList;
} & Event;

type SpeechRecognitionErrorEvent = {
  readonly error: string;
} & Event;

type ISpeechRecognition = {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
} & EventTarget;

type ISpeechRecognitionConstructor = {
  new (): ISpeechRecognition;
};

declare global {
  interface Window {
    SpeechRecognition: ISpeechRecognitionConstructor | undefined;
    webkitSpeechRecognition: ISpeechRecognitionConstructor | undefined;
  }
}

const voiceApi = new VoiceActivityApi();
const activityApi = new ActivityApi();

const buildActivityFromDraft = (
  draft: VoiceActivityDraft,
  date: Date,
  creatorTag: string,
): Activity => {
  const [hours, minutes] = draft.startTime.split(':').map(Number);
  const startDate = draft.date ? new Date(`${draft.date}T00:00:00`) : new Date(date);
  startDate.setHours(hours ?? 0, minutes ?? 0, 0, 0);

  return {
    activityId: 0,
    activityName: draft.activityName,
    creatorTag,
    startDate,
    duration: draft.duration,
    description: '',
    picture: '',
    video: '',
    recurringActivity: null,
    location: null,
  };
};

const VoiceActivityCreator = ({ open, onClose, onSaved }: VoiceActivityCreatorProps) => {
  const { user } = useContext(AppContext);
  const { currentClient } = useContext(SupervisorContext);
  const { t } = useTranslation();
  const effectiveChildMode = useEffectiveChildMode({ currentClient });
  const { setMood, setCompanionSpeech } = useCharacter();
  const [state, setState] = useState<VoiceState>('idle');
  const [transcript, setTranscript] = useState('');
  const [clarificationQuestion, setClarificationQuestion] = useState<string>('');
  const [activities, setActivities] = useState<VoiceActivityDraft[]>([]);
  const [error, setError] = useState<string | null>(null);

  const recognitionRef = useRef<ISpeechRecognition | null>(null);
  // Färdigt igenkänd text från AVSLUTADE igenkännings-sessioner (onend → omstart).
  const committedRef = useRef('');
  // Färdigt igenkänd text från den PÅGÅENDE sessionen. Hålls separat därför att
  // varje session har sin egen kumulativa results-lista, som läses om i sin helhet
  // vid varje onresult — se kommentaren där.
  const sessionFinalRef = useRef('');

  // Allt som hunnit bli färdigt igenkänt, oavsett hur många sessioner det tog.
  const capturedText = useCallback(() => committedRef.current + sessionFinalRef.current, []);

  const stopRecognition = useCallback(() => {
    recognitionRef.current?.stop();
    recognitionRef.current = null;
  }, []);

  const startRecording = useCallback(() => {
    const SpeechRecognitionClass = window.SpeechRecognition ?? window.webkitSpeechRecognition;

    if (!SpeechRecognitionClass) {
      setError('Din webbläsare stöder inte röstigenkänning.');
      return;
    }

    const recognition = new SpeechRecognitionClass();
    recognition.lang = 'sv-SE';
    recognition.interimResults = true;
    recognition.continuous = true;

    committedRef.current = '';
    sessionFinalRef.current = '';
    setTranscript('');

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      // HELA results-listan läses om, inte bara fönstret [resultIndex, length).
      // Listan är kumulativ för sessionen, så en omläsning ger exakt samma sträng
      // — hanteraren blir idempotent och kan inte dubblera.
      //
      // Fönstret seedades förut med den redan ackumulerade texten. Android-Chrome
      // stöder inte continuous på riktigt och levererar om redan färdiga resultat
      // med resultIndex tillbaka på 0; varje sådan omleverans lade då till
      // meningen EN GÅNG TILL. Därav "jag ska borsta tänderna jag ska borsta
      // tänderna jag ska borsta tänderna" — och texten gick vidare till
      // tolkningen i det skicket.
      const indices = Array.from({ length: event.results.length }, (_, i) => i);
      const { final, interim } = indices.reduce(
        (acc, i) => {
          const result = event.results[i];
          if (!result) return acc;
          return result.isFinal
            ? { ...acc, final: acc.final + (result[0]?.transcript ?? '') + ' ' }
            : { ...acc, interim: acc.interim + (result[0]?.transcript ?? '') };
        },
        { final: '', interim: '' },
      );

      sessionFinalRef.current = final;
      setTranscript(committedRef.current + final + interim);
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      console.error('SpeechRecognition error:', event.error);
      if (event.error !== 'aborted') {
        setError('Röstigenkänning misslyckades. Försök igen.');
        setState('idle');
      }
    };

    recognition.onend = () => {
      // Restart automatically if we're still in recording state (browser ended due to silence/timeout)
      if (recognitionRef.current) {
        try {
          recognitionRef.current.start();
          // Först när omstarten lyckats flyttas sessionens färdiga text över: den
          // NYA sessionen får en egen, tom results-lista, så utan flytten skrivs
          // sessionFinalRef över och det sagda försvinner. Ordningen spelar roll —
          // om start() kastar ligger den gamla sessionens resultat kvar i listan,
          // och en tidig flytt hade räknat dem två gånger.
          committedRef.current += sessionFinalRef.current;
          sessionFinalRef.current = '';
        } catch {
          // Recognition already stopped intentionally, ignore
        }
      }
    };

    recognitionRef.current = recognition;
    recognition.start();
    setState('recording');
  }, []);

  useEffect(() => {
    if (open && state === 'idle') {
      startRecording();
    }
    if (!open) {
      stopRecognition();
      setState('idle');
      setTranscript('');
      setActivities([]);
      setError(null);
      committedRef.current = '';
      sessionFinalRef.current = '';
    }
  }, [open, state, startRecording, stopRecognition]);

  useEffect(() => {
    return () => {
      stopRecognition();
    };
  }, [stopRecognition]);

  // Kompanjon-respons: driv avatarens humör + pratbubbla efter röstläget state.
  // ENDAST i barnläge — får aldrig läcka till vuxen-/handledarläge (röstknappen
  // är universell). Bubblan får aldrig fastna: vid fel/Web Speech-saknad sätts
  // voiceError, och vid stängning/unmount nollställs allt till idle.
  useEffect(() => {
    if (!effectiveChildMode) {
      return;
    }
    if (!open) {
      return;
    }

    const setBubble = (key: string, mood: CharacterMood) => {
      setMood(mood);
      setCompanionSpeech(t(key) ?? '');
    };

    if (error) {
      setBubble('child.companion.voiceError', 'thinking');
    } else {
      switch (state) {
        case 'idle':
          setBubble('child.companion.voicePrompt', 'thinking');
          break;
        case 'recording':
          setBubble('child.companion.voiceListening', 'thinking');
          break;
        case 'processing':
        case 'clarifying':
          setBubble('child.companion.voiceThinking', 'thinking');
          break;
        case 'schedule':
        case 'saving':
          setBubble('child.companion.voiceSchedule', 'happy');
          break;
        default:
          break;
      }
    }

    return () => {
      setCompanionSpeech('');
      setMood('idle');
    };
  }, [effectiveChildMode, open, state, error, setMood, setCompanionSpeech, t]);

  const handleDoneRecording = useCallback(async () => {
    stopRecognition();
    const finalTranscript = capturedText() || transcript;

    if (!finalTranscript.trim()) {
      setState('recording');
      startRecording();
      return;
    }

    setState('processing');
    setError(null);

    try {
      const result = await voiceApi.parseVoiceActivities({
        transcription: finalTranscript.trim(),
        language: 'sv',
        currentDateTime: new Date().toISOString(),
      });

      if (result.clarificationNeeded && result.clarificationQuestion) {
        setClarificationQuestion(result.clarificationQuestion);
        setActivities(result.activities);
        setState('clarifying');
      } else {
        setActivities(result.activities);
        setState('schedule');
      }
    } catch (err) {
      console.error('Voice parse error:', err);
      setError('Kunde inte tolka din inspelning. Försök igen.');
      setState('recording');
      startRecording();
    }
  }, [transcript, stopRecognition, startRecording]);

  const handleClarificationAnswer = useCallback(
    async (answer: string) => {
      setState('processing');
      setError(null);

      const combined = `${capturedText() || transcript} (förtydligande: ${answer})`;

      try {
        const result = await voiceApi.parseVoiceActivities({
          transcription: combined.trim(),
          language: 'sv',
          currentDateTime: new Date().toISOString(),
        });

        setActivities(result.activities);
        setState('schedule');
      } catch (err) {
        console.error('Voice clarification parse error:', err);
        setError('Kunde inte tolka förtydligandet. Visar det vi har.');
        setState('schedule');
      }
    },
    [transcript],
  );

  const handleSkipClarification = useCallback(() => {
    setState('schedule');
  }, []);

  const handleEdit = useCallback((index: number, updated: VoiceActivityDraft) => {
    setActivities((prev) => prev.map((a, i) => (i === index ? updated : a)));
  }, []);

  const handleDelete = useCallback((index: number) => {
    setActivities((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!user?.tag) {
      setError('Kunde inte hämta din användare. Ladda om sidan och försök igen.');
      return;
    }
    setState('saving');
    const today = new Date();

    const results = await Promise.all(
      activities.map((draft) =>
        activityApi.createActivity({
          activity: buildActivityFromDraft(draft, today, user.tag),
          supervisedTag: currentClient ? currentClient.tag : undefined,
        }),
      ),
    );

    const anyFailed = results.some((r) => r instanceof ResponseError || r === undefined);
    if (anyFailed) {
      console.error('One or more activities failed to save:', results);
      setError('En eller flera aktiviteter kunde inte sparas. Försök igen.');
      setState('schedule');
      return;
    }

    onSaved();
  }, [activities, onSaved, user, currentClient]);

  const handleCancel = useCallback(() => {
    stopRecognition();
    onClose();
  }, [stopRecognition, onClose]);

  const renderContent = () => {
    if (error) {
      return (
        <Typography color="error" variant="body2" textAlign="center" py={2}>
          {error}
        </Typography>
      );
    }

    switch (state) {
      case 'recording':
        return (
          <VoiceRecorder
            transcript={transcript}
            onDone={handleDoneRecording}
            onCancel={handleCancel}
            child={effectiveChildMode}
          />
        );

      case 'processing':
        return (
          <Stack alignItems="center" spacing={2} py={4}>
            <CircularProgress color="primary" />
            <Typography
              variant="body1"
              color="text.secondary"
              sx={effectiveChildMode ? { fontSize: 17 } : undefined}
            >
              Planerar din dag...
            </Typography>
          </Stack>
        );

      case 'clarifying':
        return (
          <ClarificationCard
            question={clarificationQuestion}
            onAnswer={handleClarificationAnswer}
            onSkip={handleSkipClarification}
            child={effectiveChildMode}
          />
        );

      case 'schedule':
      case 'saving':
        return (
          <DayScheduleView
            activities={activities}
            date={new Date()}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onConfirm={handleConfirm}
            onCancel={handleCancel}
            saving={state === 'saving'}
            child={effectiveChildMode}
          />
        );

      default:
        return null;
    }
  };

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onOpen={() => undefined}
      onClose={handleCancel}
      disableSwipeToOpen
      PaperProps={{
        sx: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          maxHeight: '85vh',
        },
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 4,
          bgcolor: 'grey.300',
          borderRadius: 2,
          mx: 'auto',
          mt: 1.5,
          mb: 0.5,
        }}
      />
      <Paper
        elevation={0}
        sx={{
          px: 3,
          pb: 3,
          pt: 1,
          overflowY: 'auto',
        }}
      >
        {renderContent()}
      </Paper>
    </SwipeableDrawer>
  );
};

export default VoiceActivityCreator;
